import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWebsitesStore } from "../../store/websitesStore";
import { decodeTokenService } from "../../services/decodeTokenService";
import { getAbandonedCarts } from "../../services/funnels";
import { logErrorToSentry } from "../../utils/handleApi";
import { dictionary, Language } from "../../utils/dictionary";
import { getStartDate, Period, PERIODS } from "../../utils/const";
import { formatDateInZone } from "../../utils/formatters";
import PeriodSelect from "../../pages/AnaliticsPage/PeriodSelect";
import Loader from "../common/Loader";

interface CustomerData {
  name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address?: string | null;
  address_detail?: string | null;
  city?: string | null;
  department?: string | null;
}

interface AbandonedCartProduct {
  name: string;
  images: string[];
  units: number;
  unit_price: number;
  unit_price_with_discount: number;
  total_value_with_discount: number;
  product_id: string;
}

interface AbandonedCart {
  session_id: string;
  website_id: string;
  last_stage: string;
  customer_data: CustomerData;
  first_seen_at: string;
  last_seen_at: string;
  products?: AbandonedCartProduct[];
}


const LAST_STAGE_LABEL_KEY: Record<string, string> = {
  addCheckoutInfo:  "abandoned-stage-checkoutForm",
  addPaymentInfo:   "abandoned-stage-paymentModal",
};

const AbandonedCartsTable = () => {
  const { language, timezone } = useAuth();
  const { websites, initialize } = useWebsitesStore();
  const [date, setDate] = useState<Period>(Period.AllDates);
  const [showPeriodSelect, setShowPeriodSelect] = useState(false);
  const [carts, setCarts] = useState<AbandonedCart[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (websites.length === 0) initialize();
  }, []);

  useEffect(() => {
    const owner_id = decodeTokenService.getUserIdFromToken();
    if (!owner_id) return;

    const period = PERIODS.find((p) => p.key === date);
    const payload = {
      owner_id,
      website_id: "",
      created_at: getStartDate(new Date(), period?.value || 0),
    };

    setIsLoading(true);
    getAbandonedCarts(payload)
      .then((res) => setCarts(Array.isArray(res) ? res : []))
      .catch((err) => { logErrorToSentry(err); setCarts([]); })
      .finally(() => setIsLoading(false));
  }, [date]);

  const getFunnelName = (websiteId: string) => {
    const site = websites.find((w) => w.websiteId === websiteId);
    return site?.name || site?.productName || "—";
  };

  const selectedPeriodLabel = dictionary[date]?.[language] ?? date;

  const headers = [
    dictionary["customer-name"]?.[language] || "Nombre cliente",
    "Email",
    dictionary["phone-label"]?.[language] || "Teléfono",
    dictionary["last-stage-reached"]?.[language] || "Última etapa",
    dictionary["last-activity"]?.[language] || "Última actividad",
    "",
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
        <p className="text-sm text-gray-500 max-w-lg">
          {dictionary["abandoned-carts-description"]?.[language]}
        </p>

        {/* Period selector */}
        <div className="relative shrink-0">
          <button
            onClick={() => setShowPeriodSelect(!showPeriodSelect)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-purple-300 transition-colors"
          >
            {selectedPeriodLabel}
            <ChevronDown
              size={14}
              className={`text-gray-400 transition-transform ${showPeriodSelect ? "rotate-180" : ""}`}
            />
          </button>
          {showPeriodSelect && (
            <PeriodSelect
              selected={date}
              onSelect={(p) => {
                setDate(p);
                setShowPeriodSelect(false);
              }}
              onClose={() => setShowPeriodSelect(false)}
            />
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Loader />
        </div>
      ) : carts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm">{dictionary["no-abandoned-carts"]?.[language]}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" style={{ minWidth: "700px" }}>
            <thead className="bg-gray-50">
              <tr>
                {headers.map((header, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {carts.map((cart) => {
                const funnelName = getFunnelName(cart.website_id);
                return (
                  <tr key={cart.session_id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-900 max-w-[160px] truncate">
                      {[cart.customer_data.name, cart.customer_data.last_name].filter(Boolean).join(" ") || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 text-sm text-blue-500 max-w-[200px] truncate">
                      {cart.customer_data.email || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {cart.customer_data.phone || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
                        {dictionary[LAST_STAGE_LABEL_KEY[cart.last_stage]]?.[language as Language] ?? cart.last_stage}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDateInZone(cart.last_seen_at, timezone)}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <Link
                        to={`/abandoned-cart/${cart.session_id}`}
                        state={{ cart, funnelName }}
                        className="text-blue-500 whitespace-nowrap text-xs hover:underline"
                      >
                        {dictionary["view-details"]?.[language] || "Ver detalles"}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AbandonedCartsTable;
